<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
  // Modo embebido (celular · menú ⋮): renderiza solo la lista Ordenar/Canal,
  // sin su propio botón ni dropdown flotante. Lo usa ChatListHeader.
  embedded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();

const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');
const chatChannelFilter = useMapGetter('getChatChannelFilter');

const [showActionsDropdown, toggleDropdown] = useToggle();

const currentStatusFilter = computed(() => {
  return chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN;
});

const currentSortBy = computed(() => {
  return (
    chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
});

const currentChannel = computed(() => chatChannelFilter.value || 'all');

const chatStatusOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    value: 'open',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    value: 'all',
  },
]);

// Simplificado a 2 (Victor 2026-05-20 · variante D): quitar Creado el,
// Prioridad, Respuesta pendiente — confunden y no se usan.
const chatSortOptions = computed(() => [
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_desc.TEXT'),
    value: 'last_activity_at_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_asc.TEXT'),
    value: 'last_activity_at_asc',
  },
]);

// Filtro por canal (Victor 2026-05-20): cliente-side en ChatList, mapea
// inbox.channel_type → categoría. Default 'all' (sin filtro).
const chatChannelOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_CHANNEL_FILTER_ITEMS.whatsapp.TEXT'),
    value: 'whatsapp',
  },
  {
    label: t('CHAT_LIST.CHAT_CHANNEL_FILTER_ITEMS.instagram.TEXT'),
    value: 'instagram',
  },
  {
    label: t('CHAT_LIST.CHAT_CHANNEL_FILTER_ITEMS.facebook.TEXT'),
    value: 'facebook',
  },
  {
    label: t('CHAT_LIST.CHAT_CHANNEL_FILTER_ITEMS.tiktok.TEXT'),
    value: 'tiktok',
  },
  {
    label: t('CHAT_LIST.CHAT_CHANNEL_FILTER_ITEMS.all.TEXT'),
    value: 'all',
  },
]);

const saveSelectedFilter = (type, value) => {
  updateUISettings({
    conversations_filter_by: {
      status: type === 'status' ? value : currentStatusFilter.value,
      order_by: type === 'sort' ? value : currentSortBy.value,
      channel: type === 'channel' ? value : currentChannel.value,
    },
  });
};

const handleStatusChange = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  saveSelectedFilter('status', value);
};

const handleSortChange = value => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  saveSelectedFilter('sort', value);
};

const handleChannelChange = value => {
  emit('changeFilter', value, 'channel');
  store.dispatch('setChatChannelFilter', value);
  saveSelectedFilter('channel', value);
};
</script>

<template>
  <div class="relative flex">
    <NextButton
      v-if="!embedded"
      v-tooltip.right="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
      icon="i-lucide-arrow-up-down"
      slate
      faded
      xs
      @click="toggleDropdown()"
    />
    <!-- Filtro reorganizado (Victor 2026-05-20 · variante D): Ordenar →
         Canal → Estado · tipográfico puro, monocromo, sin iconos. -->
    <div
      v-if="embedded || showActionsDropdown"
      v-on-click-outside="() => { if (!embedded) toggleDropdown(); }"
      :class="embedded
        ? 'w-full'
        : 'mt-2 bg-n-solid-3 border border-n-strong w-64 rounded-xl p-2 absolute z-[60] top-full ltr:left-0 rtl:right-0 shadow-[0_18px_48px_rgba(0,0,0,0.6)] max-h-[70vh] overflow-y-auto'"
    >
      <div class="px-2.5 pt-1 pb-1.5 text-[10px] font-bold tracking-wide uppercase text-n-slate-11">
        {{ $t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
      </div>
      <button
        v-for="o in chatSortOptions"
        :key="`so-${o.value}`"
        type="button"
        class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left hover:bg-n-alpha-2"
        :class="o.value === chatSortFilter ? 'bg-n-alpha-2 text-n-slate-12 font-semibold' : 'text-n-slate-12'"
        @click="handleSortChange(o.value)"
      >
        <span class="flex-1 truncate">{{ o.label }}</span>
        <span v-if="o.value === chatSortFilter">✓</span>
      </button>
      <div class="my-1.5 border-t border-n-weak" />
      <div class="px-2.5 pt-1 pb-1.5 text-[10px] font-bold tracking-wide uppercase text-n-slate-11">
        {{ $t('CHAT_LIST.CHAT_SORT.CHANNEL') }}
      </div>
      <button
        v-for="o in chatChannelOptions"
        :key="`ch-${o.value}`"
        type="button"
        class="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left hover:bg-n-alpha-2"
        :class="o.value === currentChannel ? 'bg-n-alpha-2 text-n-slate-12 font-semibold' : 'text-n-slate-12'"
        @click="handleChannelChange(o.value)"
      >
        <span class="flex-1 truncate">{{ o.label }}</span>
        <span v-if="o.value === currentChannel">✓</span>
      </button>
      <!-- Sección "Estado" ocultada (Victor 2026-05-20): se controla desde
           las tabs (Todos / Mi Chat / Urgentes / Cerrados …); el dropdown
           solo expone Ordenar y Canal para no duplicar. -->
    </div>
  </div>
</template>
